import { useEffect, useState } from "react";

import { FieldValues, Path, useWatch, type UseFormReturn } from "react-hook-form";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";

interface UseImageUploaderFieldParams<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  isUploadingName: Path<T>;
  required: boolean | undefined;
}

export const useImageUploaderField = <T extends FieldValues>({
  form,
  name,
  isUploadingName,
  required,
}: UseImageUploaderFieldParams<T>) => {
  const { t } = useTranslate();
  const { control, register } = form;

  const isUploading = useWatch({ control, name: isUploadingName });
  const value = useWatch({ control, name });
  const defaultFileUrl = form.getValues(name);
  const [fileUrl, setFileUrl] = useState<string | undefined | null>(defaultFileUrl ?? undefined);

  useEffect(() => {
    if (value === undefined) {
      register(name, isRequired(Boolean(required) && !fileUrl, "common.fileRequired"));
    }
  }, [name, fileUrl, required, value, register]);

  useEffect(() => {
    register(isUploadingName, {
      validate: (value: unknown) => {
        if (value === true) {
          return t("dashboard", "uploadingImage.pleaseWait");
        }
        return true;
      },
    });
  }, [register, isUploadingName, t]);

  return { fileUrl, setFileUrl, value, isUploading };
};
